import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Cart from "./product/Cart";
import { MenuIcon, ShoppingCartIcon, X } from "lucide-react";
import { clearCart } from "../slice/cartSlice";
import { fetchBasicConfig } from "../slice/configSlice";
import { FaUser } from "react-icons/fa";
import { fetchCartItemsData } from "../slice/cartItemsSlice";
import "react-toastify/dist/ReactToastify.css";
export default function Header() {
  const [showCart, setShowCart] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const cart = useSelector((store) => store.cart.items);
  const {
    cartItemsState: { loading: cartItemsLoading },
  } = useSelector((state) => state.cartItems);
  const dispatch = useDispatch();
  const history = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const cartRef = useRef(null);

  const { basicConfig } = useSelector((state) => state.configs);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
      if (
        showCart &&
        cartRef.current &&
        !cartRef.current.contains(event.target) &&
        !event.target.closest(".cart-toggle-btn")
      ) {
        setShowCart(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef, cartRef, showCart]);

  // Prevent scrolling when cart is open
  useEffect(() => {
    if (showCart) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showCart]);
  const [cartItems, setCartItems] = useState([]);
  useEffect(() => {
    if (showCart) {
      dispatch(fetchCartItemsData({ items: cart })).then((response) => {
        const data = response.payload?.data;

        if (data.status) {
          setCartItems(data?.data?.cartproducts);
        }
      });
    }
  }, [showCart, cart]);

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  const handleLogOut = () => {
    localStorage.setItem("token", "");
    dispatch(clearCart());
    toast.success("LogOut successful");
    history("/auth");
  };

  const isToken = localStorage.getItem("token");

  const handleOrderHistory = () => {
    history("/orderhistory");
    setMenuOpen(false);
  };

  const handleNavigation = (path) => {
    history(path);
    setMenuOpen(false);
  };

  const router = useNavigate();

  const handleCheckout = () => {
    if (cart?.length > 0) {
      setShowCart(false);
      history("/checkout");
    }
  };

  useEffect(() => {
    dispatch(fetchBasicConfig());
  }, []);

  const navLinks = [
    { name: "HOME", path: "/" },
    { name: "PRODUCTS", path: "/shop" },
    { name: "ABOUT", path: "/#about" },
    { name: "CONTACT", path: "/#contact" },
  ];

  // Determine header background and text color based on route
  const isHomeRoute = location.pathname === "/";
  const headerBackgroundClass = isHomeRoute
    ? "bg-opacity-0 bg-red-300 shadow-none"
    : "bg-white shadow-md";
  const textColorClass = isHomeRoute ? "text-black" : "text-gray-700";
  const isAuthenticated = localStorage.getItem("token");
  return (
    <>
      <header
        className={`sticky top-0 z-50 w-full ${headerBackgroundClass}  transition-all duration-300 z-10`}
        style={isHomeRoute ? { backgroundColor: "white" } : {}}
      >
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex-shrink-0">
              {basicConfig?.logo ? (
                <a href="/">
                  <img
                    src={basicConfig?.logo}
                    alt="Company Logo"
                    className="h-10 md:h-16 w-10 md:w-[66px]"
                  />
                </a>
              ) : (
                <div className="h-10 w-32 rounded animate-pulse text-center">
                  {" "}
                  Verdical
                </div>
              )}
            </div>

            {/* Right side controls */}
            <div className="flex items-center space-x-2 md:space-x-6">
              {/* Navigation Links - Desktop Only */}
              <div className="hidden md:flex items-center space-x-6 mr-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`${textColorClass} hover:text-blue-600 font-medium text-sm transition-colors duration-200 no-underline`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>

              {isAuthenticated && (
                <button
                  className="cart-toggle-btn relative p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => setShowCart(true)}
                  aria-label="Shopping Cart"
                >
                  {/* Cart button content */}
                  <ShoppingCartIcon className="h-6 w-6 text-gray-700" />
                  {cart?.length > 0 ? (
                    <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {cart?.length}
                    </span>
                  ) : (
                    <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {cart?.length}
                    </span>
                  )}
                </button>
              )}

              {!isAuthenticated && (
                <button
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full transition-colors"
                  onClick={() => router("/auth")}
                >
                  Login
                </button>
              )}

              {/* Menu Button */}

              {isAuthenticated && (
                <div className="relative" ref={menuRef}>
                  <button
                    className={`p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 ${
                      isHomeRoute ? "hover:bg-white/10" : ""
                    }`}
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label="Menu"
                  >
                    <FaUser className={`h-5 w-5 ${textColorClass}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {menuOpen && (
                    <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50 transition-all duration-200 ease-in-out">
                      {/* Mobile Navigation Links - Only visible on mobile */}
                      <div className="md:hidden border-b border-gray-100">
                        {navLinks.map((link) => (
                          <button
                            key={link.name}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 no-underline"
                            onClick={() => handleNavigation(link.path)}
                          >
                            {link.name}
                          </button>
                        ))}
                      </div>
                      <button
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={handleOrderHistory}
                      >
                        Order History
                      </button>
                      <button
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={handleLogOut}
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      {/* Cart Sidebar */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300 ${
          showCart ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          ref={cartRef}
          className={`fixed top-0 right-0 w-full sm:w-5/12 h-full bg-white shadow-xl transition-transform duration-300 transform ${
            showCart ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full">
            <div className="flex-grow overflow-y-auto p-4 border border-black">
              <Cart
                setShowCart={setShowCart}
                cart={cartItems}
                handleCheckout={handleCheckout}
                loading={cartItemsLoading}
              />
            </div>
          </div>
        </div>
      </div>

      {/* <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
      /> */}
    </>
  );
}
